import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import isClosedStage from "@app/util/isClosedStage";

// A new deal can't start in a locked funnel, nor in one whose every stage is read-only.
const isCreatableFunnel = (
  { id, stageMovesLocked }: Pick<Funnel, "id" | "stageMovesLocked">,
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly">[]>
): boolean => {
  const stages = funnelStages[id];
  return (
    !stageMovesLocked && !(stages?.length && stages.every(({ dealsReadOnly }) => dealsReadOnly))
  );
};

export const getCreatableFunnels = <T extends Pick<Funnel, "id" | "stageMovesLocked">>(
  funnels: T[],
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly">[]>
): T[] => funnels.filter((funnel) => isCreatableFunnel(funnel, funnelStages));

// A deal can't move into a funnel it couldn't start in; a deal already in it keeps it.
export const getSelectableFunnels = <T extends Pick<Funnel, "id" | "stageMovesLocked">>(
  funnels: T[],
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly">[]>,
  currentFunnelId?: Funnel["id"]
): T[] =>
  funnels.filter(
    (funnel) => funnel.id === currentFunnelId || isCreatableFunnel(funnel, funnelStages)
  );

// No deal can start in or move into a read-only stage; a deal already in one keeps it.
export const getSelectableStages = <T extends Pick<Stage, "dealsReadOnly" | "id">>(
  stages: T[],
  currentStageId?: Stage["id"]
): T[] => stages.filter(({ id, dealsReadOnly }) => !dealsReadOnly || id === currentStageId);

// The won or lost stage a Mark Won / Mark Lost action moves a deal into, unless it is read-only.
export const findClosingStage = (
  stages: Stage[],
  type: DealStageType.LOST | DealStageType.WON
): Stage | undefined => getSelectableStages(stages).find((stage) => stage.type === type);

// Reopen moves a closed deal into the funnel's last open stage. When that stage is read-only or the
// funnel is locked there is nowhere to reopen into, and Reopen isn't offered (no earlier stage).
export const getReopenStage = (stages: Stage[]): Stage | undefined => {
  if (stages[0]?.funnel?.stageMovesLocked) {
    return undefined;
  }
  const lastOpenStage = stages
    .filter((stage) => !isClosedStage(stage))
    .reduce<
      Stage | undefined
    >((result, stage) => (!result || stage.displayOrder > result.displayOrder ? stage : result), undefined);
  return lastOpenStage?.dealsReadOnly ? undefined : lastOpenStage;
};

// A funnel's lock rides on its stages, which the deal store keeps in step with each saved funnel.
export const getStagesFunnel = (
  funnelStages: Record<Funnel["id"], Pick<Stage, "funnel">[]>,
  funnelId: Funnel["id"]
): Pick<Funnel, "id" | "stageMovesLocked"> =>
  funnelStages[funnelId]?.[0]?.funnel ?? { id: funnelId };

// A layout or entry point may default a new deal into a funnel or stage it can't start in.
export const getSelectableNewDealDefaults = (
  defaults: { funnelId?: Funnel["id"]; stageId?: Stage["id"] },
  funnels: Pick<Funnel, "id" | "stageMovesLocked">[],
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly" | "id">[]>
): { funnelId?: Funnel["id"]; stageId?: Stage["id"] } => {
  const { funnelId, stageId } = defaults;
  if (!funnelId) {
    return defaults;
  }
  const funnel = funnels.find(({ id }) => id === funnelId) ?? { id: funnelId };
  if (!isCreatableFunnel(funnel, funnelStages)) {
    return {};
  }
  const stage = funnelStages[funnelId]?.find(({ id }) => id === stageId);
  return { funnelId, stageId: stage?.dealsReadOnly ? undefined : stageId };
};

// The same, for a caller that holds only the stored stages (the Field Agent's create editor).
export const getSelectableNewDealSeeds = (
  defaults: { funnelId?: Funnel["id"]; stageId?: Stage["id"] },
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly" | "funnel" | "id">[]>
): { funnelId?: Funnel["id"]; stageId?: Stage["id"] } =>
  getSelectableNewDealDefaults(
    defaults,
    defaults.funnelId ? [getStagesFunnel(funnelStages, defaults.funnelId)] : [],
    funnelStages
  );
